import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { computeMinDimensions, IataSourceLink, SNUB_NOSED_EXAMPLES } from '../data/iataRules';
import { track, PREFILL_KEY } from '../utils/track';

const FIELDS = [
  ['a', 'A — Length: nose to root of tail', "Measure from the tip of the nose to where the tail meets the body. Don't include the tail itself."],
  ['b', 'B — Height: ground to elbow', 'Measure from the floor to the elbow joint of a front leg, with your pet standing.'],
  ['c', 'C — Width: widest point', 'Measure across the widest part of your pet, usually the shoulders. Measure straight across, not around the body.'],
  ['d', 'D — Height: ground to top of head or ear tip', 'With your pet standing, measure from the floor to the top of the head or the tip of the ears, whichever is higher. For upright ears, measure to the ear tip.'],
];
const IN = 2.54;
const Toggle = ({ value, options, onChange, testid }) => (
  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid={testid}>
    {options.map(([v, label]) => (
      <button key={String(v)} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`px-3 py-1.5 text-sm ${value === v ? 'bg-blue-600 text-white' : 'bg-white text-slate-700'}`}>{label}</button>
    ))}
  </div>
);

export default function CrateCalculator() {
  const navigate = useNavigate();
  const [v, setV] = useState({ unit: 'cm', petType: 'dog', a: '', b: '', c: '', d: '', bedding: '', snub: false, animals: 1 });
  const [result, setResult] = useState(null);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const ready = ['a', 'b', 'c', 'd'].every((k) => Number(v[k]) > 0);

  const calculate = () => {
    const f = v.unit === 'in' ? IN : 1;
    const cm = Object.fromEntries(['a', 'b', 'c', 'd', 'bedding'].map((k) => [k, (Number(v[k]) || 0) * f]));
    const min = computeMinDimensions({ ...cm, brachycephalic: v.snub, animals: v.animals });
    const show = (x) => Math.round((x / f) * 10) / 10;
    setResult({ L: show(min.length), W: show(min.width), H: show(min.height) });
    track('calculator_used', { pet_type: v.petType, unit: v.unit, snub_nosed: v.snub, animals: v.animals });
  };

  const bridge = () => {
    sessionStorage.setItem(PREFILL_KEY, JSON.stringify({ unit: v.unit, a: v.a, b: v.b, c: v.c, d: v.d, bedding: v.bedding || '0', brachycephalic: v.snub, animals: v.animals, petType: v.petType }));
    navigate('/', { state: { prefill: true } });
  };
  const u = v.unit === 'in' ? 'in' : 'cm';

  return (
    <section data-testid="crate-calculator" className="bg-white border-2 border-blue-200 rounded-2xl p-6 mb-10">
      <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-900 mb-5">
        <strong>Before you measure:</strong> Use a soft tape measure. Measure with your pet standing naturally on all four feet, not stretched or sitting. If two numbers seem close, round up.
      </div>
      <div className="flex flex-wrap gap-4 mb-5">
        <Toggle testid="calc-unit" value={v.unit} onChange={set('unit')} options={[['cm', 'cm'], ['in', 'inches']]} />
        <Toggle testid="calc-pet" value={v.petType} onChange={set('petType')} options={[['dog', 'Dog'], ['cat', 'Cat']]} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, help]) => (
          <label key={k} className="block">
            <span className="text-sm font-semibold text-slate-900">{label}</span>
            <input data-testid={`calc-${k}`} type="number" min="0" step="0.1" value={v[k]} onChange={(e) => set(k)(e.target.value)}
              className="mt-1 w-full h-10 border border-slate-300 rounded-md px-3" />
            <span className="text-xs text-slate-500">{help}</span>
          </label>
        ))}
        <label className="block">
          <span className="text-sm font-semibold text-slate-900">Bedding thickness (optional)</span>
          <input data-testid="calc-bedding" type="number" min="0" step="0.1" placeholder="0" value={v.bedding} onChange={(e) => set('bedding')(e.target.value)}
            className="mt-1 w-full h-10 border border-slate-300 rounded-md px-3" />
          <span className="text-xs text-slate-500">The thickness of the mat or absorbent bedding you'll put in the crate, typically 2–5 cm. It's added to the height.</span>
        </label>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-semibold text-slate-900 block mb-1">Snub-nosed breed?</span>
            <Toggle testid="calc-snub" value={v.snub} onChange={set('snub')} options={[[true, 'Yes'], [false, 'No']]} />
            <p className="text-xs text-slate-500 mt-1">Short-nosed (brachycephalic) breeds such as {SNUB_NOSED_EXAMPLES}. IATA requires a crate 10% larger for these breeds.</p>
          </div>
          <div>
            <span className="text-sm font-semibold text-slate-900 block mb-1">Number of animals in the crate</span>
            <Toggle testid="calc-animals" value={v.animals} onChange={set('animals')} options={[[1, '1'], [2, '2'], [3, '3']]} />
            <p className="text-xs text-slate-500 mt-1">IATA allows some animals to travel together in one crate. Width increases with each animal. Check with your airline first, as many accept only one per crate.</p>
          </div>
        </div>
      </div>
      <Button data-testid="calc-submit" disabled={!ready} onClick={calculate} className="mt-6 bg-blue-600 hover:bg-blue-700">Calculate minimum crate size</Button>

      {result && (
        <div data-testid="calc-result" className="mt-8">
          <h2 className="text-xl font-bold text-slate-900 mb-3">Minimum inner crate size for your {v.petType}</h2>
          <table className="w-full max-w-sm text-sm border border-slate-200">
            <tbody>
              {[['Length', result.L, 'calc-result-length'], ['Width', result.W, 'calc-result-width'], ['Height', result.H, 'calc-result-height']].map(([k, val, id]) => (
                <tr key={k} className="border-b border-slate-200"><th className="text-left p-2">{k}</th><td className="p-2" data-testid={id}>{val} {u}</td></tr>
              ))}
            </tbody>
          </table>
          <details className="mt-3 text-sm text-slate-700">
            <summary className="cursor-pointer font-medium">How we got these numbers</summary>
            <ul className="mt-2 space-y-1">
              <li>Length = A + ½ B = {v.a} + {Number(v.b) / 2}{v.snub ? ' (+10%)' : ''} = {result.L}</li>
              <li>Width = C × {v.animals + 1} = {v.c} × {v.animals + 1}{v.snub ? ' (+10%)' : ''} = {result.W} <em>(× 3 for two animals, × 4 for three)</em></li>
              <li>Height = D + bedding = {v.d} + {v.bedding || 0}{v.snub ? ' (+10%)' : ''} = {result.H}</li>
              {v.snub && <li>Snub-nosed breed: +10% applied to all three dimensions</li>}
            </ul>
          </details>
          <p className="mt-3 text-sm text-slate-700"><strong>Important:</strong> these are <strong>inside</strong> dimensions. Crate makers often list outside dimensions, which are larger. Measure the inside of your crate, or check the manufacturer's inner measurements.</p>
          <p className="mt-2 text-xs text-slate-500">This is the IATA minimum. Your airline may have its own size limits or crate rules. This result is not a guarantee of acceptance.</p>

          <div data-testid="calc-bridge" className="mt-6 bg-slate-900 text-white rounded-xl p-6">
            <h3 className="text-lg font-bold mb-2">Size is one of eight things checked at the airport.</h3>
            <p className="text-slate-300 text-sm mb-4">A crate can be the right size and still be refused for plastic-only clips, missing ventilation, bowls you can't reach from outside, or a missing "Live Animal" label. Upload three photos and we'll check the rest.</p>
            <Button data-testid="calc-bridge-btn" onClick={bridge} className="bg-white text-slate-900 hover:bg-slate-100">Check my crate for $39 — measurements already filled in</Button>
            <p className="text-slate-400 text-xs mt-3">Your four measurements carry over, so you only add the crate's inner size and three photos. PDF report usually in a few minutes. Full refund if we miss an IATA requirement visible in your photos and measurements.</p>
          </div>
        </div>
      )}
      <IataSourceLink className="mt-6" />
    </section>
  );
}
